import { useEffect, useRef, useState } from "react";
import { Link, useParams, type To } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { priceBasisLabel } from "@shared-core/domain/customerPriceBasis";
import { customerLabelOf } from "@shared-core/domain/customer-option-wording";
import {
  addToBasketBlockedReason,
  resolveCatalogueItemPrice,
  unchosenOptionLabels,
  type CatalogueItemPrice,
} from "@shared-core/domain/catalogue-item-price";
import { askUntilPriced } from "@shared-core/domain/price-still-coming";
import { useIsStalled } from "@shared-core/domain/useIsStalled";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import type {
  CustomerCatalogueOption,
  CustomerPriceQuote,
  CustomerQuantityLadderRow,
  CustomerSpeedChoice,
} from "@shared-core/data/customer-api-types";
import {
  askAtChosenSpeed,
  deliveryDateOptions,
} from "@shared-core/domain/delivery-dates";
import { quantityStepUp } from "@shared-core/domain/quantity-step-up";
import { extractFieldErrors } from "@shared-core/data/validation";
import {
  VERSIONS_FIELD_KEY,
  ONE_VERSION,
  quantityStepOf,
  versionCountOf,
  versionsFieldMessages,
  versionsProblem,
  versionsRequestFields,
  type VersionsValue,
} from "@shared-core/domain/line-versions";
import {
  optionRequirementHint,
  unavailableOptionCodes,
} from "@shared-core/domain/catalogue-option-requirements";
import type { CatalogueField } from "@shared-core/data/catalogue-types";
import { usePortalViewer } from "./PortalViewerContext";
import {
  CardOnlyNotice,
  isCardOnly,
  OrderingBlockedNotice,
  refusalReason,
} from "./OrderingBlockedNotice";
import { OptionGrid, OptionGroup } from "./catalogue-item/OptionGroup";
import { CompactQuantityLadder } from "./catalogue-item/CompactQuantityLadder";
import { MoreOptions } from "./catalogue-item/MoreOptions";
import {
  layOutOptionGroups,
  needsAttention,
  summariseSelections,
  isTypedPaperPending,
  visibleSpecLine,
} from "./catalogue-item/option-groups";
import {
  AboutThisProduct,
  hasAboutContent,
} from "./catalogue-item/AboutThisProduct";
import { ArrivalLine } from "./catalogue-item/ArrivalLine";
import { DeliveryDateChoice } from "./catalogue-item/DeliveryDateChoice";
import { useCustomerBasket } from "./basket/CustomerBasketProvider";
import { LastOrderedLine } from "./catalogue-item/LastOrderedLine";
import { ProductCrossSell } from "./catalogue-item/ProductCrossSell";
import { ProductGallery } from "./catalogue-item/ProductGallery";
import { QuantityStepUpNudge } from "./catalogue-item/QuantityStepUpNudge";
import { renderKindFor } from "../customer-pages/ProductRender";
import { AddToBasket } from "./catalogue-item/AddToBasket";
import { QuotedToOrderNotice } from "./catalogue-item/QuotedToOrderNotice";
import { VersionsControl } from "./catalogue-item/VersionsControl";
import { POLLARDS_PHONE } from "./portalContact";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { Badge } from "../../app/components/primitives/badge";
import { cn } from "../../app/components/primitives/utils";
import { ArrowLeft, AlertTriangle } from "lucide-react";

// J1's configure and price steps (brief §8), laid out as an ecommerce product
// page: the gallery on the left, a sticky buy box on the right (price, options,
// quantity ladder with a step-up nudge, dispatch line), the item's description
// and highlighted options under the gallery, and a cross-sell row below: the item's
// related items, or failing those the rest of the customer's own catalogue.
// The buy box's one order button is "Add to basket" (portal ordering, decision
// B2: no "Order now"); checkout is on the basket pages. It draws nothing in a
// build with no basket.

// Sticky only where the whole buy box fits beside the scrolling page.
const STICKY_WHEN_TALL_ENOUGH =
  "[@media(min-width:768px)_and_(min-height:820px)]:sticky [@media(min-width:768px)_and_(min-height:820px)]:top-4";

// How many other items the cross-sell row offers.
const MAX_CROSS_SELLS = 4;

export function PortalCatalogueItemPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const topRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  // Following a cross-sell link keeps this page mounted at the old scroll
  // position; bring the new item's top into view.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    topRef.current?.scrollIntoView({ block: "start" });
  }, [itemId]);

  // Keyed on the item so following a cross-sell link starts from that item's
  // own defaults rather than the previous item's configuration.
  return (
    <div ref={topRef} className="scroll-mt-4">
      <CatalogueItemView key={itemId} itemId={itemId ?? ""} />
    </div>
  );
}

function CatalogueItemView({ itemId }: { itemId: string }) {
  const adapter = useCustomerDataAdapter();
  const { viewKey, buildPortalUrl, sessionState } = usePortalViewer();
  const customerCode =
    sessionState.status === "success"
      ? (sessionState.data.ordering_customer_code ?? undefined)
      : undefined;

  const { state: eligibilityState } = useListQuery(() => {
    if (!customerCode) return Promise.resolve(null);
    return adapter.getOrderingEligibility(customerCode);
  }, [adapter, viewKey, customerCode]);
  const { state: itemState } = useListQuery(
    () => adapter.getCatalogueItem(itemId, customerCode),
    [adapter, viewKey, itemId, customerCode],
  );
  // Merchandising comes from this GET only: the price POST returns none, so
  // reading it from there would blank it on the first option change.
  const merchandising =
    itemState.status === "success" ? itemState.data?.merchandising : null;
  const relatedItems = merchandising?.related_items ?? [];
  // The customer's own catalogue is only the cross-sell fallback, fetched once
  // the item has loaded and turned out to have no related items.
  const needsCatalogueFallback =
    itemState.status === "success" &&
    itemState.data !== null &&
    relatedItems.length === 0;
  const { state: catalogueState } = useListQuery(
    () =>
      needsCatalogueFallback
        ? adapter.listCatalogueItems()
        : Promise.resolve([]),
    [adapter, viewKey, needsCatalogueFallback],
  );

  // The basket read says whether the customer chooses their delivery date (the `customer_delivery_dates` feature).
  // Off, or with no basket, the page prices and adds at the item's default exactly as before and sends no date.
  const basket = useCustomerBasket();
  const hasDeliveryDates =
    basket?.state.status === "success" &&
    basket.state.data.delivery_dates_enabled;
  // The date the customer picked, by its Speed's code; null until they pick one (the item's default is priced).
  const [chosenSpeedCode, setChosenSpeedCode] = useState<string | null>(null);
  const askedSpeedCode = hasDeliveryDates ? chosenSpeedCode : null;

  const [configuration, setConfiguration] = useState<Record<string, string>>(
    {},
  );
  const [quantity, setQuantity] = useState<number | null>(null);
  // The item's Versions count (a number the customer types, 1 to 26) and how the quantity is shared between the
  // versions. A count of 1 is no count: the configuration carries it only above 1.
  const [versionsCountRaw, setVersionsCountRaw] = useState("1");
  const [versionsRest, setVersionsRest] = useState<
    Pick<VersionsValue, "split" | "entered">
  >({ split: ONE_VERSION.split, entered: ONE_VERSION.entered });
  const versionsValue: VersionsValue = {
    count: versionCountOf(versionsCountRaw),
    ...versionsRest,
  };
  const versionStep = quantityStepOf(
    itemState.status === "success" && itemState.data
      ? itemState.data.quantities
      : [],
  );
  const versionsUnsettled =
    quantity !== null
      ? versionsProblem(versionsValue, quantity, versionStep)
      : null;
  const versionsRequest = versionsRequestFields(versionsValue);
  const pricingConfiguration: Record<string, string> =
    versionsValue.count > 1
      ? { ...configuration, [VERSIONS_FIELD_KEY]: String(versionsValue.count) }
      : configuration;
  const { state: priceState, retry: retryPrice } = useListQuery(
    ({ isCurrent }) => {
      if (
        itemState.status !== "success" ||
        !itemState.data ||
        itemState.data.quoted_to_order ||
        quantity === null ||
        versionsUnsettled !== null
      ) {
        return Promise.resolve(null);
      }
      // A price still being worked out is asked for again until it settles; the page reads "Pricing…" meanwhile
      // (a customer chooses a Speed, never a supplier, so no route is named).
      const itemId = itemState.data.item_id;
      return askUntilPriced(
        () =>
          askAtChosenSpeed(
            (speedCode) =>
              adapter.priceCatalogueItem(itemId, {
                configuration: pricingConfiguration,
                quantity,
                customer_code: customerCode,
                needed_by_is_hard: false,
                ...(speedCode !== null && { speed_code: speedCode }),
                ...(versionsRequest.versionSplit
                  ? { version_split: versionsRequest.versionSplit }
                  : {}),
                ...(versionsRequest.versionQuantities
                  ? { version_quantities: versionsRequest.versionQuantities }
                  : {}),
              }),
            askedSpeedCode,
          ),
        { isCurrent },
      );
    },
    [
      adapter,
      viewKey,
      itemState,
      quantity,
      JSON.stringify(pricingConfiguration),
      customerCode,
      askedSpeedCode,
      versionsUnsettled?.code,
      JSON.stringify(versionsRequest),
    ],
  );
  const priceOutcome = resolveCatalogueItemPrice(priceState, quantity);
  const isPriceStalled = useIsStalled(
    priceState.status === "loading",
    priceState,
  );

  // Keep the last settled price on screen (dimmed) while a new one is being
  // fetched, rather than flashing "Pricing…" on every click. Any answer that is
  // not a price (none for this selection, a failure, a refusal) clears it: the
  // old price is not the new selection's.
  const [settledPrice, setSettledPrice] = useState<SettledPrice | null>(null);
  useEffect(() => {
    if (priceOutcome.kind === "pending") return;
    if (priceOutcome.kind === "priced" && quantity !== null) {
      setSettledPrice({
        price: priceOutcome.price,
        ladder: priceOutcome.ladder,
        quantity,
        isDeliveryIncluded: priceOutcome.isDeliveryIncluded,
        speeds:
          priceState.status === "success"
            ? (priceState.data?.speeds ?? null)
            : null,
      });
      return;
    }
    setSettledPrice(null);
    // Re-settle only when a new price response arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceState]);

  // Seed defaults once the item detail arrives.
  useEffect(() => {
    if (itemState.status !== "success" || !itemState.data) return;
    setConfiguration((prev) =>
      Object.keys(prev).length > 0
        ? prev
        : Object.fromEntries(
            choiceOptions(itemState.data!).map((o) => [
              o.key,
              o.default_code ?? "",
            ]),
          ),
    );
    setQuantity((prev) => prev ?? itemState.data!.quantities[0] ?? null);
  }, [itemState]);

  if (
    sessionState.status === "loading" ||
    eligibilityState.status === "loading" ||
    itemState.status === "loading"
  ) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        Loading…
      </Card>
    );
  }

  const eligibility =
    eligibilityState.status === "success" ? eligibilityState.data : null;
  const refusal = refusalReason(eligibility);
  if (refusal) {
    return (
      <div className="space-y-5">
        <BackLink buildPortalUrl={buildPortalUrl} />
        <OrderingBlockedNotice reason={refusal} />
      </div>
    );
  }

  if (itemState.status === "error") {
    return (
      <div className="space-y-5">
        <BackLink buildPortalUrl={buildPortalUrl} />
        <Card className="flex flex-col items-center gap-2 p-8 text-center">
          <AlertTriangle className="size-6 text-muted-foreground" />
          <p className="font-medium">We couldn&apos;t load this item</p>
          <p className="text-sm text-muted-foreground">{itemState.message}</p>
        </Card>
      </div>
    );
  }

  const item = itemState.data;
  // The Speed the latest price is at, and when it arrives (the price
  // response's Speeds, else the item's own).
  const speeds =
    (priceState.status === "success" ? priceState.data?.speeds : null) ??
    item?.speeds ??
    null;
  const arrival =
    speeds?.speeds.find((s) => s.speed_code === speeds.selected_code)
      ?.sentence ?? null;
  // With delivery dates the choice stays on the last priced Speeds while a new price is fetched, showing the date
  // just picked as selected, rather than flashing back to the item's own first read.
  const pricedSpeeds = settledPrice?.speeds ?? item?.speeds ?? null;
  const deliveryDateChoice: CustomerSpeedChoice | null =
    pricedSpeeds && priceState.status === "loading" && chosenSpeedCode
      ? { ...pricedSpeeds, selected_code: chosenSpeedCode }
      : pricedSpeeds;
  // Whether the feature is on is only known once the basket read settles (or there is no basket). Until then,
  // show nothing delivery-related rather than the legacy Speed-named arrival line, which the feature hides.
  const deliveryDatesFeatureKnown =
    !basket || basket.state.status !== "loading";
  if (!item) {
    return (
      <div className="space-y-5">
        <BackLink buildPortalUrl={buildPortalUrl} />
        <Card className="p-8 text-center text-sm text-muted-foreground">
          We can&apos;t find this item in your catalogue.
        </Card>
      </div>
    );
  }

  const isRepricing = priceState.status === "loading";
  const priceBlocked = priceOutcome.kind === "blocked";
  const priceFailed = priceOutcome.kind === "failed";
  // No price for this selection (a failure, or an answer with none): no price on the ladder either (the
  // quantities stay visible), until the next reprice succeeds.
  const isQuoteOnRequest = priceOutcome.kind === "quote_on_request";
  const hasNoPrice =
    priceFailed || priceOutcome.kind === "price_on_request" || isQuoteOnRequest;
  const price = hasNoPrice ? null : (settledPrice?.price ?? null);
  const quantityLadder =
    priceFailed || isQuoteOnRequest
      ? []
      : priceOutcome.kind === "price_on_request"
        ? priceOutcome.ladder
        : (settledPrice?.ladder ?? item.quantity_ladder ?? []);
  const stepUp = hasNoPrice ? null : quantityStepUp(quantityLadder, quantity);
  const priceBlockedMessage =
    priceOutcome.kind === "blocked" ? priceOutcome.message : null;
  const options = choiceOptions(item);
  const hasVersions = item.options.some((o) => o.key === VERSIONS_FIELD_KEY);
  const unchosenOptions = unchosenOptionLabels(options, configuration);
  // The server's own refusal of the versions' quantities, bound beside them rather than read as "no price".
  const versionMessages = versionsFieldMessages(
    priceState.status === "error" ? extractFieldErrors(priceState.error) : [],
  );
  const versionsUnsettledMessage =
    versionsUnsettled?.message ??
    versionMessages.quantities ??
    versionMessages.split ??
    null;
  // A paper we price by hand, chosen but not yet named: nothing to send us yet.
  const isTypedPaperUnnamed = options.some((option) =>
    isTypedPaperPending(option, configuration[option.key] ?? ""),
  );
  const addBlockedReason = versionsUnsettledMessage
    ? versionsUnsettledMessage
    : isTypedPaperUnnamed
      ? "Name the paper you want, then add it to your basket."
      : price
        ? null
        : addToBasketBlockedReason({
            outcome: priceOutcome,
            unchosenOptions,
            isStalled: isPriceStalled,
          });

  // Choices the current selection rules out (R28), in the shared helper's field shape.
  const requirementFields: CatalogueField[] = options.map((option) => ({
    key: option.key,
    label: customerLabelOf(option),
    mode: "choice" as const,
    options: option.choices.map((choice) => ({
      code: choice.code,
      label: customerLabelOf(choice),
      isDefault: choice.code === option.default_code,
      requires: choice.requires,
    })),
  }));
  const unavailableByOption = unavailableOptionCodes(
    requirementFields,
    configuration,
  );
  // Why each ruled-out choice is ruled out, for the dropdowns' disabled items.
  const requirementHintsFor = (option: CustomerCatalogueOption) =>
    new Map(
      option.choices.flatMap((choice) => {
        const hint = optionRequirementHint(
          requirementFields
            .find((f) => f.key === option.key)
            ?.options?.find((o) => o.code === choice.code) ?? {
            code: choice.code,
            label: customerLabelOf(choice),
            isDefault: false,
          },
          requirementFields,
        );
        return hint ? [[choice.code, hint] as const] : [];
      }),
    );

  const otherCatalogueItems =
    relatedItems.length === 0 && catalogueState.status === "success"
      ? catalogueState.data
          .filter((other) => other.item_id !== item.item_id)
          .slice(0, MAX_CROSS_SELLS)
      : [];

  const optionLayout = layOutOptionGroups(options);
  const moreOptionsNeedingAttention = optionLayout.more
    .filter((option) =>
      needsAttention(
        option,
        configuration[option.key] ?? option.default_code ?? "",
        unavailableByOption.get(option.key),
      ),
    )
    .map((option) => option.key);
  const renderOptionGroup = (option: CustomerCatalogueOption) => (
    <OptionGroup
      key={option.key}
      option={option}
      itemId={itemId}
      unavailableCodes={unavailableByOption.get(option.key)}
      requirementHints={requirementHintsFor(option)}
      mostPopularCode={merchandising?.most_popular_choice_codes?.[option.key]}
      value={configuration[option.key] ?? option.default_code ?? ""}
      onChange={(value) =>
        setConfiguration((prev) => ({
          ...prev,
          [option.key]: value,
        }))
      }
    />
  );

  const renderKind = renderKindFor(item);
  const selectedChoices = options.flatMap((option) => {
    const choice = option.choices.find(
      (c) => c.code === configuration[option.key],
    );
    return choice ? [{ option, choice }] : [];
  });
  const monochrome = selectedChoices.some(
    ({ option, choice }) =>
      (option.key === "colours" && choice.code === "mono") ||
      /black only/i.test(choice.label),
  );
  const glossy = selectedChoices.some(({ choice }) =>
    /gloss/i.test(`${choice.code} ${choice.label}`),
  );
  const caption = visibleSpecLine(options, configuration);
  const showAbout = hasAboutContent(merchandising);
  const crossSell =
    relatedItems.length > 0
      ? { heading: "Complete the set", items: relatedItems }
      : { heading: "More from your catalogue", items: otherCatalogueItems };

  return (
    <div className="space-y-10">
      <BackLink buildPortalUrl={buildPortalUrl} />

      {isCardOnly(eligibility) && <CardOnlyNotice />}

      <div className="grid grid-cols-[minmax(0,1fr)] gap-x-10 gap-y-8 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <div className="md:col-start-1">
          <ProductGallery
            kind={renderKind}
            productName={item.name}
            monochrome={monochrome}
            glossy={glossy}
            caption={caption}
          />
        </div>

        {/* Beside the gallery and, when there is one, the About section. */}
        <aside
          className={cn(
            "md:col-start-2 md:row-start-1 md:self-start",
            showAbout ? "md:row-span-2" : "md:row-span-1",
            STICKY_WHEN_TALL_ENOUGH,
          )}
        >
          <Card className="gap-0 space-y-5 p-5 md:p-6">
            <div className="space-y-1">
              <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                {item.product_family}
              </p>
              <h1 className="text-2xl font-semibold tracking-tight">
                {item.name}
              </h1>
              {item.customer_product_ref && (
                <p className="text-sm text-muted-foreground">
                  Your ref {item.customer_product_ref}
                </p>
              )}
              {merchandising?.last_ordered && (
                <LastOrderedLine lastOrdered={merchandising.last_ordered} />
              )}
            </div>

            {item.quoted_to_order ? (
              <QuotedToOrderNotice />
            ) : priceBlocked ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border bg-muted/30 p-6 text-center">
                <Badge variant="neutral">Contact your rep</Badge>
                <p className="max-w-sm text-sm text-muted-foreground">
                  {priceBlockedMessage}
                </p>
              </div>
            ) : (
              <>
                {versionsUnsettledMessage ? (
                  <p
                    role="status"
                    className="text-sm text-muted-foreground"
                    data-testid="price-versions-unsettled"
                  >
                    No price yet: settle the quantity of each version below.
                  </p>
                ) : (
                  <PriceBlock
                    price={price}
                    quantity={settledPrice?.quantity ?? null}
                    isDeliveryIncluded={
                      settledPrice?.isDeliveryIncluded ?? false
                    }
                    isRepricing={isRepricing}
                    outcome={priceOutcome}
                    isStalled={isPriceStalled}
                    onRetry={retryPrice}
                  />
                )}

                <div className="space-y-5">
                  <OptionGrid>
                    {optionLayout.inline.map(renderOptionGroup)}
                  </OptionGrid>

                  {optionLayout.more.length > 0 && (
                    <MoreOptions
                      summary={summariseSelections(
                        optionLayout.more,
                        configuration,
                      )}
                      attentionKeys={moreOptionsNeedingAttention}
                    >
                      <OptionGrid>
                        {optionLayout.more.map(renderOptionGroup)}
                      </OptionGrid>
                    </MoreOptions>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Quantity</label>
                    <CompactQuantityLadder
                      rows={quantityLadder}
                      quantities={item.quantities}
                      selectedQuantity={quantity}
                      onSelect={(q) => setQuantity(q)}
                    />
                    {stepUp && (
                      <QuantityStepUpNudge
                        stepUp={stepUp}
                        onSelect={(q) => setQuantity(q)}
                      />
                    )}
                  </div>

                  {hasVersions && quantity !== null && (
                    <VersionsControl
                      label="Versions"
                      countRaw={versionsCountRaw}
                      value={versionsValue}
                      quantity={quantity}
                      quantityStep={versionStep}
                      errors={versionMessages}
                      onCountChange={(raw, next) => {
                        setVersionsCountRaw(raw);
                        setVersionsRest({
                          split: next.split,
                          entered: next.entered,
                        });
                      }}
                      onValueChange={(next) =>
                        setVersionsRest({
                          split: next.split,
                          entered: next.entered,
                        })
                      }
                    />
                  )}
                </div>

                {hasDeliveryDates
                  ? deliveryDateChoice &&
                    price && (
                      <DeliveryDateChoice
                        options={deliveryDateOptions(deliveryDateChoice)}
                        onSelect={setChosenSpeedCode}
                      />
                    )
                  : deliveryDatesFeatureKnown &&
                    arrival && <ArrivalLine sentence={arrival} />}

                <AddToBasket
                  itemId={item.item_id}
                  configuration={pricingConfiguration}
                  versionSplit={versionsRequest.versionSplit}
                  versionQuantities={versionsRequest.versionQuantities}
                  quantity={quantity}
                  expectedPricePence={
                    price && !isRepricing ? price.price_pence : null
                  }
                  isQuoteOnRequest={isQuoteOnRequest && !isRepricing}
                  disabledReason={addBlockedReason}
                  // The date the price on screen is at: what the customer saw is what is added.
                  speedCode={
                    hasDeliveryDates
                      ? (settledPrice?.speeds?.selected_code ?? null)
                      : null
                  }
                  added={{
                    itemName: item.name,
                    kind: renderKind,
                    monochrome,
                    glossy,
                    configurationLines: selectedChoices.map(
                      ({ option, choice }) =>
                        `${customerLabelOf(option)}: ${customerLabelOf(choice)}`,
                    ),
                    quantity: quantity ?? 0,
                    totalPence: isQuoteOnRequest
                      ? null
                      : (price?.price_pence ?? 0),
                  }}
                  crossSell={crossSell}
                  buildPortalUrl={buildPortalUrl}
                />
              </>
            )}
          </Card>
        </aside>

        {showAbout && merchandising && (
          <div className="md:col-start-1">
            <AboutThisProduct
              description={merchandising.description}
              highlights={merchandising.highlights}
            />
          </div>
        )}
      </div>

      {relatedItems.length > 0 ? (
        <ProductCrossSell
          heading="Complete the set"
          items={relatedItems}
          buildPortalUrl={buildPortalUrl}
        />
      ) : (
        <ProductCrossSell
          heading="More from your catalogue"
          items={otherCatalogueItems}
          buildPortalUrl={buildPortalUrl}
        />
      )}
    </div>
  );
}

/** The option groups the customer chooses between: not the item's Versions count, which has its own control. */
function choiceOptions(item: {
  options: CustomerCatalogueOption[];
}): CustomerCatalogueOption[] {
  return item.options.filter((o) => o.key !== VERSIONS_FIELD_KEY);
}

interface SettledPrice {
  price: CustomerPriceQuote;
  ladder: CustomerQuantityLadderRow[];
  quantity: number;
  /** The line's price includes delivery (a supplier that delivers); otherwise delivery is chosen at checkout. */
  isDeliveryIncluded: boolean;
  /** The delivery dates this price was answered with; null when the answer carried none. */
  speeds: CustomerSpeedChoice | null;
}

function PriceBlock({
  price,
  quantity,
  isDeliveryIncluded,
  isRepricing,
  outcome,
  isStalled,
  onRetry,
}: {
  price: CustomerPriceQuote | null;
  quantity: number | null;
  isDeliveryIncluded: boolean;
  isRepricing: boolean;
  outcome: CatalogueItemPrice;
  isStalled: boolean;
  onRetry: () => void;
}) {
  if (outcome.kind === "failed") {
    return (
      <div role="alert" className="space-y-2 text-sm text-muted-foreground">
        <p>{outcome.message}</p>
        <p>
          You can also ring us on {POLLARDS_PHONE} and we&apos;ll price it for
          you.
        </p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  if (outcome.kind === "quote_on_request") {
    return (
      <div className="space-y-1">
        <p className="text-2xl font-semibold tracking-tight">
          Quote on request
        </p>
        <p className="text-sm text-muted-foreground">
          Our team prices the paper you named. Add it to your basket, then send
          the basket to us and we&apos;ll come back with a quote for everything
          in it.
        </p>
      </div>
    );
  }
  if (outcome.kind === "price_on_request") {
    return (
      <div className="space-y-1">
        <p className="text-2xl font-semibold tracking-tight">
          Price on request
        </p>
        <p className="text-sm text-muted-foreground">
          We don&apos;t have an online price for this selection yet, so it
          can&apos;t be ordered online. Ring us on {POLLARDS_PHONE} and
          we&apos;ll quote it, or try a different option or quantity.
        </p>
      </div>
    );
  }
  if (!price) {
    return isStalled ? (
      <div role="status" className="space-y-2 text-sm text-muted-foreground">
        <p>Pricing is taking longer than usual.</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    ) : (
      <p className="text-sm text-muted-foreground">Pricing…</p>
    );
  }
  return (
    <div
      className={cn(
        "space-y-0.5 transition-opacity",
        isRepricing && "opacity-50",
      )}
      aria-busy={isRepricing}
    >
      <p className="flex items-baseline gap-2">
        <span className="text-4xl font-semibold tracking-tight tabular-nums">
          {formatQuotedPrice(price.price_pence)}
        </span>
        <span className="text-sm text-muted-foreground">
          {isDeliveryIncluded ? "ex VAT · delivery included" : "ex VAT"}
        </span>
      </p>
      <p className="text-sm tabular-nums">
        {formatQuotedPrice(price.unit_price_pence)} each
        {quantity !== null && (
          <span className="text-muted-foreground">
            {" "}
            · {quantity.toLocaleString("en-GB")} in total
          </span>
        )}
      </p>
      <p className="text-xs text-muted-foreground">
        {priceBasisLabel(price.basis)}
        {price.basis_date ? ` · set ${price.basis_date}` : ""}
      </p>
    </div>
  );
}

function BackLink({
  buildPortalUrl,
}: {
  buildPortalUrl: (path: string) => To;
}) {
  return (
    <Link
      to={buildPortalUrl(paths.portalCatalogue())}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-4" />
      Catalogue
    </Link>
  );
}
